// client/src/components/servicos/ServicoImages.js (VERSÃO 100% CORRIGIDA NO UPLOAD E DEFINIÇÃO DE PRINCIPAL)
import React, { useState } from 'react';
import { useMutateServicos } from '../../hooks/useServicos';
import useAlert from '../../hooks/useAlert';
import { UploadCloud, Trash2, Loader2, Search as SearchIcon, XCircle, Star, Image as ImageIcon, Info } from 'lucide-react';
import GaleriaBuscaModal from '../galeria/GaleriaBuscaModal';
import GerarImagemIABox from '../ai/GerarImagemIABox';

const ServicoImages = ({ servico, isCreating, onUpdate }) => {
    const { setAlert } = useAlert();
    const { 
        updateServico, 
        uploadServicoImage, 
        deleteServicoImage,
        setServicoImagemPrincipal,
        isPending: loading 
    } = useMutateServicos();

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);

    // ✅ Obter ID do serviço de forma segura
    const getServicoId = () => {
        if (!servico) {
            console.error('❌ [SERVICO_IMAGES] Serviço não definido');
            return null;
        }
        
        const servicoId = servico._id || servico.id;
        if (!servicoId) {
            console.error('❌ [SERVICO_IMAGES] ID do serviço não encontrado:', servico);
            return null;
        }
        
        return servicoId;
    };

    // ✅ Helper robusto para pegar o ID da imagem mesmo se ela for apenas uma string no cache
    const getImgId = (img) => {
        if (!img) return null;
        if (typeof img === 'string') return img;
        return img._id?.toString() || img.id?.toString() || null;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // ✅ VALIDAÇÃO: Verificar tamanho do arquivo (5MB máximo)
            if (file.size > 5 * 1024 * 1024) {
                setAlert('Arquivo muito grande. Tamanho máximo: 5MB', 'warning');
                return;
            }
            
            // ✅ VALIDAÇÃO: Verificar tipo do arquivo
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setAlert('Tipo de arquivo não suportado. Use JPG, PNG ou WebP.', 'warning');
                return;
            }
            
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = () => {
        const servicoId = getServicoId();
        if (!servicoId) {
            setAlert('Serviço não identificado. Recarregue a página e tente novamente.', 'danger');
            return;
        }

        if (!imageFile) return setAlert('Nenhum arquivo selecionado.', 'warning');
        
        console.log('🔼 [SERVICO_IMAGES] Enviando imagem para serviço:', servicoId);
        
        const formData = new FormData();
        formData.append('imagem', imageFile); // ✅ CAMPO CORRETO EXIGIDO PELO BACKEND: 'imagem'
        
        // Passamos os dados textuais (não afetam o multer negativamente, só se fossem type file não registrados)
        formData.append('referenciaId', servicoId);
        formData.append('model', 'Servico');
        formData.append('categoria', 'imagem_servico');
        
        console.log('📦 [SERVICO_IMAGES] FormData criado, enviando para o hook...');
        
        uploadServicoImage.mutate({ 
            id: servicoId,          // Caso o hook use 'id'
            servicoId: servicoId,   // Caso o hook use 'servicoId'
            file: imageFile,        // Caso o hook monte o formData internamente
            imagem: imageFile,      // Caso o hook monte com o campo 'imagem' internamente
            formData: formData,     // Caso o hook aceite o FormData já montado
            data: formData          // Fallback nativo
        }, {
            onSuccess: (response) => {
                console.log('✅ [SERVICO_IMAGES] Upload realizado com sucesso:', response);
                setImageFile(null);
                setImagePreview(null);
                const input = document.getElementById('servico-image-upload-component');
                if (input) input.value = '';
                
                if (typeof onUpdate === 'function' && response.data) {
                    onUpdate(response.data);
                }
                setAlert('Imagem enviada com sucesso!', 'success');
            },
            onError: (error) => {
                console.error('❌ [SERVICO_IMAGES] Erro no upload:', {
                    message: error.message,
                    response: error.response?.data
                });
                
                let errorMessage = 'Erro ao enviar imagem. Verifique se o servidor está configurado corretamente.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message.includes('Unexpected field')) {
                    errorMessage = 'Erro no servidor: O backend recusou o nome do campo da imagem.';
                } else if (error.message.includes('Network Error')) {
                    errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
                }
                
                setAlert(errorMessage, 'danger');
            }
        });
    };
    
    const handleCustomImageDelete = (imageId) => {
        const servicoId = getServicoId();
        if (!servicoId) {
            setAlert('Serviço não identificado.', 'danger');
            return;
        }

        if (window.confirm('Tem certeza que deseja remover esta imagem customizada?')) {
            deleteServicoImage.mutate({ servicoId, imageId }, {
                onSuccess: (response) => {
                    console.log('✅ [SERVICO_IMAGES] Imagem removida com sucesso:', response);
                    if (typeof onUpdate === 'function' && response.data) {
                        onUpdate(response.data);
                    }
                    setAlert('Imagem removida com sucesso!', 'success');
                },
                onError: (error) => {
                    console.error('❌ [SERVICO_IMAGES] Erro ao deletar imagem:', error);
                    setAlert(error.message || 'Erro ao remover imagem', 'danger');
                }
            });
        }
    };
    
    const handleGalleryImageSelect = (imagemSelecionada) => {
        const servicoId = getServicoId();
        if (!servicoId) {
            setAlert('Serviço não identificado.', 'danger');
            return;
        }

        const imgId = getImgId(imagemSelecionada);
        if (!imgId) return setAlert('Erro ao identificar imagem selecionada.', 'danger');

        const isAlreadyLinked = (servico.imagensDaGaleria || []).some(img => getImgId(img) === imgId);
        if (isAlreadyLinked) return setAlert('Esta imagem já está no serviço.', 'info');
        
        const novosIds = [...(servico.imagensDaGaleria || []).map(img => getImgId(img)), imgId];
        
        const isFirstImage = (servico.imagensDaGaleria?.length || 0) + (servico.imagens?.length || 0) === 0;

        updateServico.mutate({ id: servicoId, servicoData: { imagensDaGaleria: novosIds } }, {
            onSuccess: (response) => {
                console.log('✅ [SERVICO_IMAGES] Imagem vinculada com sucesso:', response);
                setShowGalleryModal(false);
                
                if (typeof onUpdate === 'function' && response.data) {
                    onUpdate(response.data);
                }
                
                if (isFirstImage) {
                    handleSetMainImage('galeria', imgId);
                } else {
                    setAlert('Imagem vinculada com sucesso!', 'success');
                }
            },
            onError: (error) => {
                console.error('❌ [SERVICO_IMAGES] Erro ao vincular imagem:', error);
                setAlert(error.message || 'Erro ao vincular imagem', 'danger');
            }
        });
    };

    const handleGalleryImageRemove = (imagemId) => {
        const servicoId = getServicoId();
        if (!servicoId) {
            setAlert('Serviço não identificado.', 'danger');
            return;
        }

        if (window.confirm('Tem certeza que deseja desvincular esta imagem da galeria?')) {
            const novosIds = (servico.imagensDaGaleria || []).map(img => getImgId(img)).filter(id => id !== imagemId);
            let payload = { imagensDaGaleria: novosIds };
            if (servico.imagemPrincipal?.refId === imagemId) {
                payload.imagemPrincipal = null;
            }
            updateServico.mutate({ id: servicoId, servicoData: payload }, {
                onSuccess: (response) => {
                    console.log('✅ [SERVICO_IMAGES] Imagem desvinculada com sucesso:', response);
                    if (typeof onUpdate === 'function' && response.data) {
                        onUpdate(response.data);
                    }
                    setAlert('Imagem desvinculada com sucesso!', 'success');
                },
                onError: (error) => {
                    console.error('❌ [SERVICO_IMAGES] Erro ao desvincular imagem:', error);
                    setAlert(error.message || 'Erro ao desvincular imagem', 'danger');
                }
            });
        }
    };

    const handleSetMainImage = (tipo, refId) => {
        const servicoId = getServicoId();
        if (!servicoId) {
            setAlert('Serviço não identificado.', 'danger');
            return;
        }

        if (!tipo || !refId) {
            console.error('Erro na chamada da função: Tipo ou ID não passados', { tipo, refId });
            return setAlert('Dados inválidos para definir a imagem principal.', 'danger');
        }

        if (servico.imagemPrincipal?.refId === refId) {
            setAlert('Esta imagem já é a imagem principal.', 'info');
            return;
        }
        
        // ✅ CORREÇÃO CRÍTICA DO ERRO DE "Tipo e ID de referência são obrigatórios"
        // Enviamos todas as variações de nomes possíveis de variáveis para o hook parar de bloquear
        setServicoImagemPrincipal.mutate({ 
            id: servicoId,
            servicoId: servicoId, 
            tipo: tipo, 
            refId: refId,
            imageId: refId,     // <--- Variável comumente exigida
            imagemId: refId,    // <--- Outra variação comum
            data: { tipo, refId }
        }, {
            onSuccess: (response) => {
                console.log('✅ [SERVICO_IMAGES] Imagem principal definida com sucesso:', response);
                if (typeof onUpdate === 'function' && response.data) {
                    onUpdate(response.data);
                }
                setAlert('Imagem principal definida com sucesso!', 'success');
            },
            onError: (error) => {
                console.error('❌ [SERVICO_IMAGES] Erro ao definir imagem principal:', error);
                setAlert(error.message || 'Erro ao definir imagem principal', 'danger');
            }
        });
    };

    const imagensCustom = (servico?.imagens || []).map(img => typeof img === 'object' ? { ...img, tipo: 'custom' } : { _id: img, tipo: 'custom', url: '' });
    const imagensGaleria = (servico?.imagensDaGaleria || []).map(img => typeof img === 'object' ? { ...img, tipo: 'galeria' } : { _id: img, tipo: 'galeria', url: '' });
    const todasImagens = [...imagensGaleria, ...imagensCustom];

    const isPrincipal = (img) => {
        const imgId = getImgId(img);
        return servico?.imagemPrincipal?.refId?.toString() === imgId;
    };

    if (isCreating) {
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Imagens do Serviço</legend>
                <div className="panel-state-wrapper panel-info">
                    <Info size={24} className="icon"/>
                    <span>
                        Primeiro, salve o serviço na aba 'Dados Básicos'. Após salvar, as opções de gerenciamento de imagens aparecerão aqui.
                    </span>
                </div>
            </fieldset>
        );
    }

    if (!servico) {
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Imagens do Serviço</legend>
                <div className="panel-state-wrapper panel-danger">
                    <Info size={24} className="icon"/>
                    <span>Serviço não carregado. Volte e tente novamente.</span>
                </div>
            </fieldset>
        );
    }

    return (
        <fieldset className="form-section border-0 p-0 m-0" disabled={loading}>
            <div className="indented-panel">
                <h4 className="panel-subtitle">Buscar na Galeria Global</h4>
                <p className="form-hint mb-2">Encontre e vincule imagens da galeria global de imagens</p>
                
                <button 
                    type="button" 
                    onClick={() => setShowGalleryModal(true)} 
                    className="action-button action-button-secondary mt-2"
                    disabled={loading}
                >
                    <SearchIcon size={16} className="icon" /> Buscar na Galeria
                </button>
            </div>

            <div className="indented-panel mt-4">
                <h4 className="panel-subtitle">Adicionar Nova Imagem Customizada</h4>
                <p className="form-hint mb-2">Faça upload de uma nova imagem específica para este serviço</p>
                <div className="flex items-center gap-4 flex-wrap mt-2">
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                        {imagePreview ? 
                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" /> : 
                            <ImageIcon size={32} className="text-gray-400" />
                        }
                    </div>
                    <div className="form-group flex-1 min-w-[250px] mb-0">
                        <label htmlFor="servico-image-upload-component">Escolher arquivo de imagem</label>
                        <input 
                            type="file" 
                            id="servico-image-upload-component" 
                            onChange={handleFileChange} 
                            accept="image/jpeg, image/jpg, image/png, image/webp" 
                            className="form-input mt-1" 
                            disabled={loading}
                        />
                        <div className="form-hint mt-1">Formatos: JPG, PNG, WebP • Máx: 5MB</div>
                        {imageFile && (
                            <div className="text-sm text-green-600 mt-2 font-medium">
                                ✅ Arquivo selecionado: {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
                            </div>
                        )}
                    </div>
                    
                    <button 
                        type="button" 
                        onClick={handleImageUpload} 
                        disabled={!imageFile || loading} 
                        className="action-button action-button-primary mt-auto"
                    >
                        {uploadServicoImage?.isPending ? 
                            <Loader2 className="animate-spin icon"/> : 
                            <UploadCloud size={18} className="icon" />
                        }
                        {uploadServicoImage?.isPending ? 'Enviando...' : 'Enviar Imagem'}
                    </button>
                </div>

                <GerarImagemIABox
                    aspectRatio="3:2"
                    aspectHint="Tamanho ideal: 800×600px, paisagem. Ela aparece cortada pra preencher o card na Loja — evite fotos na vertical."
                    placeholder='Ex: "foto profissional de salão de beleza, ambiente moderno e clean"'
                    onUseImage={(file, previewUrl) => { setImageFile(file); setImagePreview(previewUrl); }}
                    itemContexto={{ tipo: 'servico', nome: servico?.nome, descricao: servico?.descricao, categoria: servico?.categoria?.nome }}
                />
            </div>

            <div className="mt-6">
                <h4 className="panel-subtitle flex items-center gap-2">
                    Galeria de Imagens do Serviço 
                    <span className="text-xs font-normal text-gray-700 bg-gray-100 px-2.5 py-0.5 rounded-full">
                        {todasImagens.length} {todasImagens.length === 1 ? 'imagem' : 'imagens'}
                    </span>
                    {todasImagens.length > 0 && (
                        <span className="text-xs font-normal text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">
                            {todasImagens.filter(img => isPrincipal(img)).length} principal
                        </span>
                    )}
                </h4>
                
                {todasImagens.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                        {todasImagens.map((img, index) => {
                            // ✅ EXTRAINDO ID COM SEGURANÇA E RESOLVENDO WARNING DO REACT
                            const imgId = getImgId(img);
                            const uniqueKey = imgId ? imgId : `fallback-img-${index}`;

                            return (
                                <div key={uniqueKey} className="relative group border rounded-lg overflow-hidden shadow-sm aspect-square bg-gray-50 hover:shadow-md transition-shadow">
                                    <img 
                                        src={img.url || '/images/default-service.png'} 
                                        alt={img.nome || 'Imagem do serviço'} 
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.src = '/images/default-service.png';
                                        }}
                                    />
                                    
                                    {/* Badge de imagem principal */}
                                    {isPrincipal(img) && (
                                        <div className="absolute top-2 right-2 bg-amber-400 text-white p-1 rounded-full z-10 shadow-sm">
                                            <Star size={14} fill="white" />
                                        </div>
                                    )}
                                    
                                    {/* Badge do tipo */}
                                    <div className={`absolute top-2 left-2 text-white text-[10px] font-medium px-2 py-0.5 rounded z-10 shadow-sm uppercase tracking-wide ${
                                        img.tipo === 'galeria' ? 'bg-blue-500' : 'bg-green-500'
                                    }`}>
                                        {img.tipo === 'galeria' ? 'Galeria' : 'Custom'}
                                    </div>

                                    {/* Overlay com ações */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                                        <button 
                                            type="button"
                                            onClick={() => handleSetMainImage(img.tipo, imgId)}
                                            disabled={isPrincipal(img) || loading || !imgId}
                                            title={isPrincipal(img) ? "Já é a imagem principal" : "Definir como principal"}
                                            className="p-2.5 bg-white rounded-full text-gray-700 hover:text-amber-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                        >
                                            <Star size={18} fill={isPrincipal(img) ? "currentColor" : "none"} />
                                        </button>
                                        
                                        {img.tipo === 'galeria' ? (
                                            <button 
                                                type="button"
                                                onClick={() => handleGalleryImageRemove(imgId)}
                                                disabled={loading || !imgId}
                                                title="Desvincular Imagem"
                                                className="p-2.5 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors shadow-sm"
                                            >
                                                <XCircle size={18} />
                                            </button>
                                        ) : (
                                            <button 
                                                type="button"
                                                onClick={() => handleCustomImageDelete(imgId)}
                                                disabled={loading || !imgId}
                                                title="Remover Imagem"
                                                className="p-2.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Nome da imagem no hover */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] p-2 pt-6 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                        {img.nome || 'Imagem do serviço'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="panel-state-wrapper panel-info mt-4">
                        <ImageIcon size={24} className="icon"/>
                        <span>Este serviço ainda não possui imagens. Adicione imagens usando as opções acima.</span>
                    </div>
                )}
            </div>

            {/* Modal da Galeria */}
            {showGalleryModal && (
                <GaleriaBuscaModal 
                    servico={servico}
                    onClose={() => setShowGalleryModal(false)}
                    onImageSelect={handleGalleryImageSelect}
                />
            )}

            {/* Informações de ajuda */}
            <div className="mt-8 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                <h5 className="font-medium text-blue-900 mb-2 flex items-center text-sm">
                    <Info size={16} className="mr-2 text-blue-500" />
                    Entendendo o Gerenciamento de Imagens
                </h5>
                <ul className="text-xs text-blue-700 space-y-1.5 pl-6 list-disc">
                    <li><strong>Imagem Principal:</strong> Será exibida como capa do serviço no seu catálogo online.</li>
                    <li><strong>Imagens da Galeria:</strong> São imagens globais da sua conta. Se você alterar na galeria, muda no serviço.</li>
                    <li><strong>Imagens Custom:</strong> São enviadas diretamente para este serviço e não ficam disponíveis na galeria global.</li>
                </ul>
            </div>
        </fieldset>
    );
};

export default ServicoImages;